import type {
  PlaycastTouchEvent,
  PlaycastTouchDetail,
  PlaycastTouchMode,
} from '../types';
import type {
  PlaycastMessage,
  PlaycastMessageTouchStart,
  PlaycastMessageTouchEnd,
  PlaycastMessageTouchMove,
  PlaycastMessageTouchCancel,
  PlaycastMessageTouchDown,
  PlaycastMessageTouchUp,
  PlaycastMessageTouchSetMode,
  PlaycastMessageTouchGetMode,
  PlaycastMessageTouchFlush,
  PlaycastMessageTouchGesture,
  PlaycastMessageSource,
} from '../types';
import { formatMessage } from './_util/formatMessage';

const target = 'touch';

// Web-standard touch events (multi-touch with changedTouches array)
export const start = (
  hostId: string,
  touchEvent: PlaycastTouchEvent,
  userAuth?: string
): PlaycastMessage<PlaycastMessageTouchStart> => {
  const messageSource: PlaycastMessageSource = 'player';
  const message: PlaycastMessageTouchStart = {
    target,
    action: 'Start',
    isReply: false,
    message: touchEvent,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageTouchStart>;
};

export const end = (
  hostId: string,
  touchEvent: PlaycastTouchEvent,
  userAuth?: string
): PlaycastMessage<PlaycastMessageTouchEnd> => {
  const messageSource: PlaycastMessageSource = 'player';
  const message: PlaycastMessageTouchEnd = {
    target,
    action: 'End',
    isReply: false,
    message: touchEvent,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageTouchEnd>;
};

export const move = (
  hostId: string,
  touchEvent: PlaycastTouchEvent,
  userAuth?: string
): PlaycastMessage<PlaycastMessageTouchMove> => {
  const messageSource: PlaycastMessageSource = 'player';
  const message: PlaycastMessageTouchMove = {
    target,
    action: 'move',
    isReply: false,
    message: touchEvent,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageTouchMove>;
};

export const cancel = (
  hostId: string,
  touchEvent: PlaycastTouchEvent,
  userAuth?: string
): PlaycastMessage<PlaycastMessageTouchCancel> => {
  const messageSource: PlaycastMessageSource = 'player';
  const message: PlaycastMessageTouchCancel = {
    target,
    action: 'Cancel',
    isReply: false,
    message: touchEvent,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageTouchCancel>;
};

// Legacy single-touch events (backwards compatibility)
export const down = (
  hostId: string,
  touchDetail: PlaycastTouchDetail,
  userAuth?: string
): PlaycastMessage<PlaycastMessageTouchDown> => {
  const messageSource: PlaycastMessageSource = 'player';
  const message: PlaycastMessageTouchDown = {
    target,
    action: 'down',
    isReply: false,
    message: touchDetail,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageTouchDown>;
};

export const up = (
  hostId: string,
  touchDetail: PlaycastTouchDetail,
  userAuth?: string
): PlaycastMessage<PlaycastMessageTouchUp> => {
  const messageSource: PlaycastMessageSource = 'player';
  const message: PlaycastMessageTouchUp = {
    target,
    action: 'up',
    isReply: false,
    message: touchDetail,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageTouchUp>;
};

// Touch mode control
export const setMode = (
  hostId: string,
  mode: PlaycastTouchMode,
  userAuth?: string
): PlaycastMessage<PlaycastMessageTouchSetMode> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageTouchSetMode = {
    target,
    action: 'setMode',
    isReply: false,
    message: mode,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageTouchSetMode>;
};

export const getMode = (hostId: string, userAuth?: string): PlaycastMessage<PlaycastMessageTouchGetMode> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageTouchGetMode = {
    target,
    action: 'getMode',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageTouchGetMode>;
};

export const flush = (hostId: string, userAuth?: string): PlaycastMessage<PlaycastMessageTouchFlush> => {
  const messageSource: PlaycastMessageSource = 'player';
  const message: PlaycastMessageTouchFlush = {
    target,
    action: 'Flush',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageTouchFlush>;
};

export const gesture = (
  hostId: string,
  gestureType: string,
  params: Record<string, unknown>,
  userAuth?: string
): PlaycastMessage<PlaycastMessageTouchGesture> => {
  const messageSource: PlaycastMessageSource = 'player';
  const message: PlaycastMessageTouchGesture = {
    target,
    action: 'gesture',
    isReply: false,
    message: {
      type: gestureType,
      params,
    },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageTouchGesture>;
};
